/*
  Reel rails, one behavior on every page (site audit 2026-10-20): homepage "See the game in action"
  (sections/pt-home-action) and deck page "See it on the court" (snippets/pt-deck-reels).
  Markup: a container [data-pt-reels="<page>"] holding <figure>s, each with
  <video muted loop playsinline preload="none" data-src data-poster data-clip>.

  - Posters load when the rail is within ~800px; videos load only when they play.
  - The single most-visible reel (60%+ in view) plays muted. Not with reduced motion or Save-Data/2g.
  - A tap plays that reel from the start with sound and quiets the rest. Tap again to pause/resume.
    A reel with sound plays once (no loop); scrolling it out of view mutes and pauses it.
  - GA: reel_play {clip, page} when sound is turned on, reel_complete at 90%.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const conn = navigator.connection;
  const lowData = !!(conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')));
  const track = (name, params) => { if (typeof window.gtag === 'function') window.gtag('event', name, params); };

  function init(rail) {
    if (rail.dataset.ptReelsReady === 'true') return;
    rail.dataset.ptReelsReady = 'true';
    const page = rail.dataset.ptReels || 'page';
    const videos = Array.from(rail.querySelectorAll('video[data-src]'));
    if (!videos.length) return;
    const fig = (v) => v.closest('figure');
    const showPoster = (v) => { if (!v.poster && v.dataset.poster) v.poster = v.dataset.poster; };
    const load = (v) => { showPoster(v); if (!v.getAttribute('src')) v.src = v.dataset.src; };
    const ratios = new Map();
    let withSound = null;

    const quiet = (v) => {
      v.muted = true;
      v.loop = true;
      fig(v).classList.remove('is-unmuted');
      if (withSound === v) withSound = null;
    };

    const autoplay = () => {
      if (withSound || reduced || lowData) return;
      let best = null;
      let bestRatio = 0.6;
      // Strictly greater, in DOM order: on desktop, where every reel is fully visible, the first one plays.
      videos.forEach((v) => { const r = ratios.get(v) || 0; if (r > bestRatio || (!best && r >= bestRatio)) { best = v; bestRatio = r; } });
      videos.forEach((v) => { if (v !== best && !v.paused) v.pause(); });
      if (best && best.paused) { load(best); best.play().catch(() => {}); }
    };

    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { videos.forEach(showPoster); near.disconnect(); }
      }, { rootMargin: '800px 0px' });
      near.observe(rail);

      const seen = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          ratios.set(e.target, e.intersectionRatio);
          if (e.target === withSound && e.intersectionRatio < 0.25) { e.target.pause(); quiet(e.target); }
        });
        autoplay();
      }, { threshold: [0, 0.25, 0.6, 0.8, 1] });
      videos.forEach((v) => seen.observe(v));
    } else {
      videos.forEach(showPoster);
    }

    videos.forEach((v) => {
      const f = fig(v);
      f.addEventListener('click', () => {
        load(v);
        if (v !== withSound) {
          videos.forEach((o) => { if (o !== v) { o.pause(); quiet(o); } });
          withSound = v;
          v.muted = false;
          v.loop = false;
          v.currentTime = 0;
          f.classList.add('is-unmuted');
          delete v.dataset.ptDone;
          v.play().catch(() => {});
          track('reel_play', { clip: v.dataset.clip, page });
        } else if (v.paused) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
      v.addEventListener('play', () => f.classList.add('is-playing'));
      ['pause', 'ended'].forEach((type) => v.addEventListener(type, () => f.classList.remove('is-playing')));
      v.addEventListener('timeupdate', () => {
        if (v !== withSound || v.dataset.ptDone || !v.duration) return;
        if (v.currentTime / v.duration >= 0.9) {
          v.dataset.ptDone = 'true';
          track('reel_complete', { clip: v.dataset.clip, page });
        }
      });
    });
  }

  const boot = () => document.querySelectorAll('[data-pt-reels]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
